import { FC, useState } from 'react'
import { Modal } from 'ui/Modal'
import { PaginatedMiniList } from 'ui/components/PaginatedMiniList'
import { ResourceListItem } from 'ui/components/ResourceListItem'
import { usePaymentTerminals } from 'api/PaymentTerminals'
import { WaitingForWearable } from './WaitingForWearable'
import { useTranslation } from 'react-i18next'
import { WaitingForPaymentCard } from './WaitingForPaymentCard'
import { usePageQueryParam } from 'hooks/UsePageQueryParam'

interface SelectTerminalModalProps {
  onClose: () => void
  isOpen: boolean
  onComplete: () => void
  isWearableAssociation?: boolean
  isAddPaymentMethod?: boolean
  orderId?: string
  customerId: string
}

export const SelectTerminalModal: FC<SelectTerminalModalProps> = ({
  onClose,
  isOpen,
  isWearableAssociation = false,
  onComplete,
  isAddPaymentMethod = false,
  orderId,
  customerId,
}) => {
  const { t } = useTranslation()

  const [, setPage] = usePageQueryParam()

  const handleClose = () => {
    setPage(1)
    onClose()
  }

  return (
    <Modal
      isOpen={isOpen}
      title={t('clients.client_profile.select_terminal_modal.title')}
      onClose={handleClose}
    >
      <SelectTerminal
        orderId={orderId}
        customerId={customerId}
        onComplete={onComplete}
        isAddPaymentMethod={isAddPaymentMethod}
        isWearableAssociation={isWearableAssociation}
      />
    </Modal>
  )
}

type SelectTerminalProps = Pick<
  SelectTerminalModalProps,
  'customerId' | 'isAddPaymentMethod' | 'isWearableAssociation' | 'onComplete' | 'orderId'
>

export const SelectTerminal: FC<SelectTerminalProps> = ({
  isWearableAssociation = false,
  isAddPaymentMethod = false,
  onComplete,
  orderId = null,
  customerId,
}) => {
  const { t } = useTranslation()
  const { data: { data: terminals = [], meta = {} } = {} } = usePaymentTerminals({
    includeKiosk: true,
  })
  const [currentStep, setCurrentStep] = useState<'select' | 'waiting'>('select')
  const [selectedTerminal, setSelectedTerminal] = useState(null)
  const [, setPage] = usePageQueryParam()

  const handleSendRequest = (selectedTerminal) => {
    const terminal = terminals.find((terminal) => terminal.id === selectedTerminal.id)
    setSelectedTerminal(terminal)
    setCurrentStep('waiting')
  }

  const handleComplete = () => {
    onComplete()
  }

  const handleBackToSelect = () => {
    setCurrentStep('select')
    setSelectedTerminal(null)
  }

  return (
    <>
      {currentStep === 'select' && (
        <>
          <div className="mb-4">
            <p className="text-sm text-gray-600">
              {t('clients.client_profile.select_terminal_modal.description')}
            </p>
          </div>

          <PaginatedMiniList
            isLoading={false}
            emptyMessage={t('clients.client_profile.select_terminal_modal.empty_message')}
            meta={meta}
            onPageChange={setPage}
            rows={terminals.map((terminal) => (
              <ResourceListItem
                key={terminal.id}
                title={terminal.name || terminal.id}
                onClick={() => handleSendRequest(terminal)}
                compact
              />
            ))}
          />
        </>
      )}

      {currentStep === 'waiting' && (
        <>
          {isWearableAssociation && (
            <WaitingForWearable
              orderId={orderId}
              customerId={customerId}
              terminal={selectedTerminal}
              onWearableAssociated={handleComplete}
              onCancel={handleBackToSelect}
            />
          )}
          {isAddPaymentMethod && (
            <WaitingForPaymentCard
              customerId={customerId}
              orderId={orderId}
              terminal={selectedTerminal}
              onPaymentMethodAdded={handleComplete}
              onCancel={handleBackToSelect}
            />
          )}
        </>
      )}
    </>
  )
}
